(() => {
  "use strict";

  const runtime = window.AssistCoClubPageRuntime || {};
  const pageApi = window.AssistCoClubPageApi;
  const params = new URLSearchParams(window.location.search);
  const pageHandle = params.get("club_page") || params.get("club");
  const itemId = params.get("club_item");
  const productPath = window.location.pathname;
  const apiBaseUrl = pageApi?.normalizeBaseUrl(runtime.apiBaseUrl);

  if (!pageHandle || !itemId || !apiBaseUrl || !pageApi) {
    return;
  }

  let lastItemSignature = "";
  let applyTimer = 0;
  let suppressMutationsUntil = 0;
  let colorLockReleaseTimer = 0;

  setColorSelectorsPending(true, { autoRelease: false });
  void init();

  async function init() {
    try {
      const context = await pageApi.fetchItemContext(apiBaseUrl, pageHandle, itemId);
      if (!context?.item || decodeURIComponent(productPath.split("/").filter(Boolean).pop()) !== context.item.productHandle) {
        setColorSelectorsPending(false);
        return;
      }

      ensurePdpStyles();
      const signature = JSON.stringify(context.item);
      lastItemSignature = signature;
      applyContext(context.item);
      observeDom(context.item);
    } catch (error) {
      setColorSelectorsPending(false);
      console.error("[AssistCoClubPagePdp]", error);
    }
  }

  function observeDom(item) {
    const observer = new MutationObserver((records) => {
      if (Date.now() < suppressMutationsUntil) {
        return;
      }

      const hasRelevantMutation = records.some((record) => {
        const target = record.target;
        if (!(target instanceof HTMLElement)) {
          return true;
        }

        if (target.closest(".assistco-club-page-pdp-overlays")) {
          return false;
        }

        if (target.closest("[data-trykk-root]")) {
          return false;
        }

        return true;
      });

      if (!hasRelevantMutation) {
        return;
      }

      scheduleApply(item);
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    document.addEventListener("change", (event) => {
      const target = event.target;
      if (!(target instanceof HTMLElement)) {
        return;
      }

      if (target.closest("[data-trykk-root]")) {
        if (isTrykkLiveInput(target)) {
          refreshOverlaysOnly(item);
          return;
        }

        scheduleApply(item);
        return;
      }

      if (target.closest("variant-selects, variant-radios, product-form")) {
        setColorSelectorsPending(true);
        scheduleApply(item);
      }
    });

    document.addEventListener("input", (event) => {
      const target = event.target;
      if (!(target instanceof HTMLElement)) {
        return;
      }

      if (target.closest("[data-trykk-root]")) {
        if (isTrykkLiveInput(target)) {
          refreshOverlaysOnly(item);
          return;
        }

        scheduleApply(item);
      }
    });

    window.addEventListener("popstate", () => scheduleApply(item));
    window.addEventListener("variant:change", () => scheduleApply(item));
  }

  function scheduleApply(item) {
    if (window.clearTimeout) {
      window.clearTimeout(applyTimer);
    }

    applyTimer = window.setTimeout(() => {
      if (lastItemSignature !== JSON.stringify(item)) {
        return;
      }
      setColorSelectorsPending(true);
      applyContext(item);
    }, 80);
  }

  function refreshOverlaysOnly(item) {
    suppressOwnMutations(240);
    const selectionState = resolveSelectionState(item);
    const presentation = resolvePresentation(item, selectionState);
    renderTrykkOverlays(item, presentation.useSecondary);
  }

  function suppressOwnMutations(duration = 160) {
    suppressMutationsUntil = Math.max(suppressMutationsUntil, Date.now() + duration);
  }

  function isTrykkLiveInput(target) {
    return target.matches(
      ".js-trykk-name, .js-trykk-number, .js-trykk-unit, .js-trykk-text, textarea, [contenteditable='true']",
    );
  }

  function applyContext(item) {
    const href = pageApi.withClubContext?.(window.location.href, productPath, pageHandle, itemId);
    if (href && href !== window.location.href) window.history.replaceState(window.history.state, "", href);
    suppressOwnMutations();
    const selectionState = resolveSelectionState(item);
    const presentation = resolvePresentation(item, selectionState);
    lockColorSelectors(item, selectionState);
    overrideProductImages(presentation.imageUrl);
    ensureClubTrykkBoxes(item);
    filterTrykkBoxes(item);
    applyTrykkBoxOverrides(item);
    renderTrykkOverlays(item, presentation.useSecondary);
  }

  function ensureClubTrykkBoxes(item) {
    if (!Array.isArray(item.trykkOptions) || item.trykkOptions.length === 0) {
      cleanupFallbackTrykkBoxes(new Set());
      return;
    }

    const root = document.querySelector("[data-trykk-root]");
    if (!(root instanceof HTMLElement)) {
      return;
    }

    const mount = root.querySelector("[data-trykk-mount]") || root;
    if (!(mount instanceof HTMLElement)) {
      return;
    }

    const sourceConfig = readTrykkRootConfig(root);
    const activeCodes = new Set();

    item.trykkOptions.forEach((option) => {
      const code = normalizeToken(option && option.code ? option.code : "");
      if (!code) {
        return;
      }

      activeCodes.add(code);
      const boxes = findTrykkBoxes(code);
      const hasNativeBox = boxes.some((box) => box.dataset.clubPageFallback !== "true");

      if (hasNativeBox) {
        boxes
          .filter((box) => box.dataset.clubPageFallback === "true")
          .forEach((box) => removeFallbackTrykkBox(box));
        return;
      }

      const fallbackBox = boxes.find((box) => box.dataset.clubPageFallback === "true");
      if (fallbackBox instanceof HTMLElement) {
        updateFallbackTrykkBox(fallbackBox, option, sourceConfig);
        return;
      }

      const createdBox = createFallbackTrykkBox(option, sourceConfig);
      if (!(createdBox instanceof HTMLElement)) {
        return;
      }

      const packTitle = resolveTrykkPackTitle(option, sourceConfig);
      const pack = findOrCreateFallbackPack(mount, packTitle, code);
      pack.appendChild(createdBox);
      bindFallbackTrykkBox(createdBox, option, sourceConfig);
    });

    cleanupFallbackTrykkBoxes(activeCodes);
    cleanupEmptyFallbackPacks();
  }

  function lockColorSelectors(item, selectionState) {
    const entries = buildAllowedColorEntries(item);
    if (entries.length === 0) {
      return;
    }

    const root = getMainProductRoot();
    if (!(root instanceof HTMLElement)) {
      return;
    }

    const selection =
      selectionState && typeof selectionState === "object"
        ? selectionState
        : resolveSelectionState(item);

    const radioFields = Array.from(root.querySelectorAll("fieldset")).filter((fieldset) =>
      isColorField(fieldset) || fieldsetHasAllowedColorOptions(fieldset, entries),
    );

    radioFields.forEach((fieldset) => {
      const radios = Array.from(fieldset.querySelectorAll('input[type="radio"]'));
      let firstMatch = null;
      let hasCheckedMatch = false;
      let visibleCount = 0;

      radios.forEach((radio) => {
        const label =
          (radio.id && fieldset.querySelector(`label[for="${cssEscape(radio.id)}"]`)) ||
          radio.closest("label");
        const nativeUnavailable = isColorOptionUnavailable(radio, label);
        const keep = matchesAnyAllowedColor(
          [
            radio.getAttribute("value"),
            radio.getAttribute("data-option-value"),
            radio.dataset.optionValue,
            radio.getAttribute("data-value"),
            label?.textContent,
          ],
          entries,
        );
        const visible = keep && !nativeUnavailable;

        radio.hidden = !visible;
        radio.tabIndex = visible ? 0 : -1;
        radio.setAttribute("aria-hidden", visible ? "false" : "true");
        toggleOptionVisibility(radio, label, fieldset, visible);

        if (visible) {
          visibleCount += 1;
        }

        if (visible && radio.checked) {
          hasCheckedMatch = true;
        }

        if (visible && !firstMatch) {
          firstMatch = radio;
        }
      });

      if (firstMatch && !hasCheckedMatch) {
        firstMatch.checked = true;
        firstMatch.dispatchEvent(new Event("change", { bubbles: true }));
      }

      toggleColorFieldVisibility(fieldset, visibleCount > 0);
    });

    const selects = Array.from(root.querySelectorAll("select")).filter((select) =>
      isColorSelect(select) || selectHasAllowedColorOptions(select, entries),
    );

    selects.forEach((select) => {
      let firstMatchValue = "";
      let currentValueAllowed = false;
      let visibleCount = 0;

      Array.from(select.options).forEach((option) => {
        const nativeUnavailable = isSelectOptionUnavailable(option);
        const keep = matchesAnyAllowedColor([option.value, option.textContent], entries);
        const visible = keep && !nativeUnavailable;

        option.dataset.clubPageManagedDisabled = keep ? "false" : "true";
        option.disabled = !visible;
        option.hidden = !visible;

        if (visible && option.value === select.value) {
          currentValueAllowed = true;
        }

        if (visible) {
          visibleCount += 1;
        }

        if (visible && !firstMatchValue) {
          firstMatchValue = option.value;
        }
      });

      if (firstMatchValue && !currentValueAllowed) {
        select.value = firstMatchValue;
        select.dispatchEvent(new Event("change", { bubbles: true }));
      }

      toggleColorFieldVisibility(select, visibleCount > 0);
    });

    setColorSelectorsPending(false);
  }

  function setColorSelectorsPending(pending, options = {}) {
    const elements = collectColorFieldElements();
    if (elements.length === 0) {
      return;
    }

    if (colorLockReleaseTimer && window.clearTimeout) {
      window.clearTimeout(colorLockReleaseTimer);
      colorLockReleaseTimer = 0;
    }

    elements.forEach((element) => {
      if (!(element instanceof HTMLElement)) {
        return;
      }

      if (pending) {
        element.dataset.clubPageColorPending = "true";
        element.style.visibility = "hidden";
      } else {
        delete element.dataset.clubPageColorPending;
        element.style.visibility = "";
      }
    });

    if (pending && options.autoRelease !== false) {
      colorLockReleaseTimer = window.setTimeout(() => {
        setColorSelectorsPending(false);
      }, 450);
    }
  }

  function collectColorFieldElements() {
    const root = getMainProductRoot();
    if (!(root instanceof HTMLElement)) {
      return [];
    }

    const elements = new Set();

    Array.from(root.querySelectorAll("fieldset")).forEach((fieldset) => {
      if (!(fieldset instanceof HTMLElement)) {
        return;
      }

      if (!isColorField(fieldset)) {
        return;
      }

      elements.add(fieldset);
      const wrapper = fieldset.closest(".product-form__input, .variant-input, .swatch, .color-swatch");
      if (wrapper instanceof HTMLElement) {
        elements.add(wrapper);
      }
    });

    Array.from(root.querySelectorAll("select")).forEach((select) => {
      if (!(select instanceof HTMLSelectElement) || !isColorSelect(select)) {
        return;
      }

      elements.add(select);
      const wrapper = select.closest(
        ".product-form__input, .variant-select, .select, .select__select, .product-form__input--dropdown",
      );
      if (wrapper instanceof HTMLElement) {
        elements.add(wrapper);
      }
    });

    return Array.from(elements);
  }

  function fieldsetHasAllowedColorOptions(fieldset, entries) {
    if (!(fieldset instanceof HTMLElement)) {
      return false;
    }

    return Array.from(fieldset.querySelectorAll('input[type="radio"]')).some((radio) => {
      const label =
        (radio.id && fieldset.querySelector(`label[for="${cssEscape(radio.id)}"]`)) ||
        radio.closest("label");
      return matchesAnyAllowedColor(
        [
          radio.getAttribute("value"),
          radio.getAttribute("data-option-value"),
          radio.dataset.optionValue,
          radio.getAttribute("data-value"),
          label?.textContent,
        ],
        entries,
      );
    });
  }

  function selectHasAllowedColorOptions(select, entries) {
    if (!(select instanceof HTMLSelectElement)) {
      return false;
    }

    return Array.from(select.options).some((option) =>
      matchesAnyAllowedColor([option.value, option.textContent], entries),
    );
  }

  function toggleOptionVisibility(radio, label, fieldset, keep) {
    if (label instanceof HTMLElement) {
      label.hidden = !keep;
      label.style.display = keep ? "" : "none";
    }

    const nodes = [
      radio.closest(".product-form__input"),
      radio.closest(".swatch-input"),
      radio.closest(".variant-input"),
      radio.closest(".color-swatch"),
      radio.closest(".form__label"),
      radio.closest(".swatch"),
      radio.closest(".swatch-element"),
      radio.closest(".variant-option"),
      radio.closest("li"),
      label instanceof HTMLElement ? label.closest("li") : null,
      label instanceof HTMLElement ? label.parentElement : null,
      radio.parentElement,
    ];

    nodes.forEach((node) => {
      if (!(node instanceof HTMLElement) || node === fieldset || node === label) {
        return;
      }

      node.hidden = !keep;
      node.style.display = keep ? "" : "none";
    });
  }

  function toggleColorFieldVisibility(control, visible) {
    const nodes = [
      control instanceof HTMLElement ? control : null,
      control instanceof HTMLElement
        ? control.closest(
            "fieldset, .product-form__input, .variant-input, .variant-select, .select, .color-swatch",
          )
        : null,
      control instanceof HTMLElement
        ? control.closest(".product-form__input, .variant-input, .variant-select, .select, .color-swatch")
        : null,
    ];

    nodes.forEach((node) => {
      if (!(node instanceof HTMLElement)) {
        return;
      }

      node.hidden = !visible;
      node.style.display = visible ? "" : "none";
    });
  }

  function isColorOptionUnavailable(radio, label) {
    if (!(radio instanceof HTMLInputElement)) {
      return true;
    }

    return (
      radio.disabled ||
      radio.getAttribute("aria-disabled") === "true" ||
      radio.closest(".disabled, .soldout, .unavailable") instanceof HTMLElement ||
      (label instanceof HTMLElement &&
        (label.classList.contains("disabled") ||
          label.classList.contains("soldout") ||
          label.classList.contains("unavailable") ||
          label.getAttribute("aria-disabled") === "true"))
    );
  }

  function isSelectOptionUnavailable(option) {
    if (!(option instanceof HTMLOptionElement)) {
      return true;
    }

    if (option.dataset.clubPageManagedDisabled !== "true") {
      option.dataset.clubPageNativeDisabled = option.disabled ? "true" : "false";
    }

    return option.dataset.clubPageNativeDisabled === "true";
  }

  function overrideProductImages(imageUrl) {
    if (!imageUrl) {
      return;
    }

    const root = getMainProductRoot();
    if (!(root instanceof HTMLElement)) {
      return;
    }

    const wrappers = collectMediaWrappers();
    const images = wrappers.flatMap((wrapper) =>
      Array.from(wrapper.querySelectorAll("img")).filter(
        (image) => !image.closest(".assistco-club-page-pdp-overlays"),
      ),
    );

    if (images.length === 0) {
      const fallbackImage = root.querySelector(
        "media-gallery .product__media-item img, .product__media img, .product-media-container img",
      );
      if (fallbackImage instanceof HTMLImageElement) {
        images.push(fallbackImage);
      }
    }

    images.forEach((image) => {
      if (!(image instanceof HTMLImageElement)) {
        return;
      }

      image.src = imageUrl;
      image.srcset = imageUrl;
      image.removeAttribute("loading");
      image.fetchPriority = "high";
      image.style.objectFit = "contain";

      const link = image.closest("a");
      if (link instanceof HTMLAnchorElement) {
        link.href = imageUrl;
      }
    });
  }

  function filterTrykkBoxes(item) {
    const root = document.querySelector("[data-trykk-root]");
    if (!(root instanceof HTMLElement)) {
      return;
    }

    const codes = new Set((item.trykkCodes || []).map((code) => normalizeToken(code)));

    if (codes.size === 0) {
      root.style.display = "none";
      return;
    }

    root.style.display = "";

    const boxes = Array.from(root.querySelectorAll("[data-trykk-box]"));
    boxes.forEach((box) => {
      if (!(box instanceof HTMLElement)) {
        return;
      }

      const code = normalizeToken(box.dataset.trykkCode || "");
      const visible = codes.has(code);
      box.hidden = !visible;
      box.style.display = visible ? "" : "none";
    });

    const packs = Array.from(root.querySelectorAll("[data-trykk-pack], .trykk-pack"));
    packs.forEach((pack) => {
      if (!(pack instanceof HTMLElement)) {
        return;
      }

      const hasVisibleBox = Array.from(pack.querySelectorAll("[data-trykk-box]")).some((box) => {
        if (!(box instanceof HTMLElement)) {
          return false;
        }

        return !box.hidden && box.style.display !== "none";
      });

      pack.hidden = !hasVisibleBox;
      pack.style.display = hasVisibleBox ? "" : "none";
    });
  }

  function applyTrykkBoxOverrides(item) {
    if (!Array.isArray(item.trykkOptions)) {
      return;
    }

    item.trykkOptions.forEach((option) => {
      const box = findTrykkBox(option.code);
      if (!(box instanceof HTMLElement)) {
        return;
      }

      const enabled = box.querySelector(".js-trykk-enabled");
      if (!(enabled instanceof HTMLInputElement)) {
        return;
      }

      const defaultOn = option.defaultSelected === true;
      const required = option.required === true;
      const locked = option.locked === true;
      const mustBeEnabled = required || locked;
      const desiredChecked = mustBeEnabled || defaultOn;
      const alreadyInitialized = box.dataset.clubPageInitialized === "true";

      box.dataset.required = String(required);
      box.dataset.defaultOn = String(desiredChecked);

      let nextChecked = enabled.checked;
      if (!alreadyInitialized) {
        nextChecked = desiredChecked;
        box.dataset.clubPageInitialized = "true";
      }
      if (mustBeEnabled) {
        nextChecked = true;
      }

      const didChange = enabled.checked !== nextChecked;
      enabled.checked = nextChecked;
      enabled.disabled = mustBeEnabled;

      const toggle = enabled.closest(".trykk-toggle");
      if (toggle instanceof HTMLElement) {
        toggle.classList.toggle("is-locked", mustBeEnabled);
      }

      syncFallbackTrykkBox(box, option);

      if (didChange) {
        enabled.dispatchEvent(new Event("change", { bubbles: true }));
      }
    });
  }

  function renderTrykkOverlays(item, useSecondary) {
    const overlays = collectActiveTrykkOverlays(item, useSecondary);
    document.querySelectorAll(".assistco-club-page-pdp-overlays").forEach((layer) => {
      if (layer instanceof HTMLElement) {
        layer.remove();
      }
    });

    const wrappers = collectMediaWrappers();
    wrappers.forEach((wrapper) => {
      if (overlays.length === 0) {
        return;
      }

      if (window.getComputedStyle(wrapper).position === "static") {
        wrapper.style.position = "relative";
      }

      const layer = document.createElement("div");
      layer.className = "assistco-club-page-pdp-overlays";
      layer.setAttribute("aria-hidden", "true");
      Object.assign(layer.style, {
        position: "absolute",
        inset: "0",
        zIndex: "4",
        pointerEvents: "none",
        overflow: "hidden",
      });

      overlays.forEach((entry) => {
        const node = buildOverlayNode(
          entry.option,
          entry.overlay,
          entry.contentText,
          entry.targetKey,
        );
        if (node) {
          layer.appendChild(node);
        }
      });

      if (layer.childElementCount > 0) {
        wrapper.appendChild(layer);
      }
    });
  }

  function collectMediaWrappers() {
    const root = getMainProductRoot();
    if (!(root instanceof HTMLElement)) {
      return [];
    }

    const seen = new Set();
    const candidates = Array.from(
      root.querySelectorAll(
        [
          "media-gallery .product__media-item img",
          "media-gallery .product__media img",
          ".product__media-wrapper img",
          ".product-media-container img",
          ".product__media-item img",
        ].join(", "),
      ),
    )
      .map((image) => {
        if (!(image instanceof HTMLImageElement)) {
          return null;
        }

        if (image.closest(".thumbnail, .slider-counter, .product__media-thumbnail")) {
          return null;
        }

        const wrapper = image.closest(
          ".product-media-container, .product__media, .product__media-wrapper, .product__media-item",
        );
        if (!(wrapper instanceof HTMLElement) || seen.has(wrapper)) {
          return null;
        }

        const rect = wrapper.getBoundingClientRect();
        if (rect.width < 180 || rect.height < 180 || !isVisibleElement(wrapper)) {
          return null;
        }

        seen.add(wrapper);
        return wrapper;
      })
      .filter(Boolean);

    const leafCandidates = candidates.filter((candidate) => {
      if (!(candidate instanceof HTMLElement)) {
        return false;
      }

      return !candidates.some(
        (other) => other instanceof HTMLElement && other !== candidate && candidate.contains(other),
      );
    });

    const activeCandidates = leafCandidates.filter((candidate) => isActiveMediaWrapper(candidate));
    const finalCandidates = activeCandidates.length > 0 ? activeCandidates : leafCandidates;

    return finalCandidates
      .sort((left, right) => scoreMediaWrapper(right) - scoreMediaWrapper(left))
      .slice(0, 1);
  }

  function getMainProductRoot() {
    const trykkRoot = document.querySelector("[data-trykk-root]");
    if (trykkRoot instanceof HTMLElement) {
      const scopedRoot = trykkRoot.closest(
        [
          '#MainContent .product',
          '[id^="MainProduct-"]',
          '.shopify-section .product',
          'main .product',
          'section .product',
        ].join(", "),
      );
      if (scopedRoot instanceof HTMLElement) {
        return scopedRoot;
      }
    }

    const selectedVariantInput = document.querySelector(
      'product-form input[name="id"], form[action*="/cart/add"] input[name="id"], select[name="id"]',
    );
    if (selectedVariantInput instanceof HTMLElement) {
      const scopedRoot = selectedVariantInput.closest(
        [
          '#MainContent .product',
          '[id^="MainProduct-"]',
          '.shopify-section .product',
          'main .product',
          'section .product',
        ].join(", "),
      );
      if (scopedRoot instanceof HTMLElement) {
        return scopedRoot;
      }
    }

    return null;
  }

  function isVisibleElement(element) {
    if (!(element instanceof HTMLElement)) {
      return false;
    }

    if (element.closest('[hidden], [aria-hidden="true"], .hidden')) {
      return false;
    }

    const style = window.getComputedStyle(element);
    if (style.display === "none" || style.visibility === "hidden" || style.opacity === "0") {
      return false;
    }

    const rect = element.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0;
  }

  function isActiveMediaWrapper(element) {
    if (!(element instanceof HTMLElement)) {
      return false;
    }

    return Boolean(
      element.closest(
        '.is-active, [aria-hidden="false"], [data-active="true"], .swiper-slide-active, .slider__slide.is-active',
      ),
    );
  }

  function scoreMediaWrapper(element) {
    if (!(element instanceof HTMLElement)) {
      return 0;
    }

    const rect = element.getBoundingClientRect();
    const activeBonus = isActiveMediaWrapper(element) ? 1_000_000 : 0;
    return activeBonus + rect.width * rect.height;
  }

  function buildOverlayNode(option, overlay, contentText, targetKey) {
    if (!overlay || overlay.enabled !== true) {
      return null;
    }

    const placement = resolveOverlayPlacement(overlay, targetKey || "single");
    const node = document.createElement("div");
    node.className = `assistco-club-page__product-overlay assistco-club-page__product-overlay--${overlay.kind || "text"}`;
    node.setAttribute("aria-hidden", "true");
    const backgroundColor = resolveOverlayBackgroundColor(overlay);
    Object.assign(node.style, {
      position: "absolute",
      left: `calc(50% + ${Number(placement.offsetX || 0)}%)`,
      top: `calc(50% + ${Number(placement.offsetY || 0)}%)`,
      transform: `translate(-50%, -50%) scale(${Number(placement.scale || 100) / 100})`,
      transformOrigin: "center center",
      opacity: `${Number(overlay.opacity || 100) / 100}`,
      color: overlay.textColor || "#111827",
      fontSize: `${Number(overlay.fontSize || 28)}px`,
      fontFamily: overlay.fontFamily || "Arial, sans-serif",
      fontWeight: overlay.fontWeight || "700",
      fontStyle: overlay.fontStyle || "normal",
      textDecoration: overlay.textDecoration || "none",
      textAlign: overlay.textAlign || "center",
      textTransform: overlay.textTransform || "uppercase",
      letterSpacing: `${Number(overlay.letterSpacing || 0)}px`,
      whiteSpace: "pre-wrap",
      lineHeight: "1.1",
      maxWidth: "82%",
      pointerEvents: "none",
      boxSizing: "border-box",
      padding: `${Number(overlay.boxPaddingY || 8)}px ${Number(overlay.boxPaddingX || 12)}px`,
      border:
        overlay.boxEnabled === true && Number(overlay.boxBorderWidth || 0) > 0
          ? `${Number(overlay.boxBorderWidth || 0)}px solid ${overlay.boxBorderColor || "#111827"}`
          : "0 solid transparent",
      borderRadius: `${Number(overlay.boxBorderRadius || 8)}px`,
      background: backgroundColor,
      textShadow: "0 2px 8px rgba(255, 255, 255, 0.65)",
    });

    if (overlay.kind === "image" && overlay.imageUrl) {
      const image = document.createElement("img");
      image.alt = "";
      image.src = overlay.imageUrl;
      Object.assign(image.style, {
        display: "block",
        maxWidth: "180px",
        maxHeight: "180px",
        objectFit: "contain",
      });
      node.appendChild(image);
      return node;
    }

    const text = document.createElement("span");
    text.textContent = String(contentText || overlay.text || option.title || "TRYKK").trim() || "TRYKK";
    text.style.display = "block";
    node.appendChild(text);
    return node;
  }

  function ensurePdpStyles() {
    const root = getMainProductRoot();
    if (root instanceof HTMLElement) {
      root.classList.add("assistco-club-page-pdp-active");
    }

    if (document.getElementById("assistco-club-page-pdp-styles")) {
      return;
    }

    const style = document.createElement("style");
    style.id = "assistco-club-page-pdp-styles";
    style.textContent = `
      .assistco-club-page-pdp-active fieldset label,
      .assistco-club-page-pdp-active .variant-input label,
      .assistco-club-page-pdp-active .product-form__input label:not(.form__label) {
        min-height: 44px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
      }

      .assistco-club-page-pdp-active [data-trykk-box] input,
      .assistco-club-page-pdp-active [data-trykk-box] select,
      .assistco-club-page-pdp-active [data-trykk-box] button {
        min-height: 44px;
      }

      .assistco-club-page-pdp-active .assistco-club-page-pdp-overlays img {
        display: block;
        max-width: min(100%, 240px);
        max-height: min(100%, 240px);
        object-fit: contain;
      }

      @media (max-width: 749px) {
        .assistco-club-page-pdp-active fieldset label,
        .assistco-club-page-pdp-active .variant-input label,
        .assistco-club-page-pdp-active .product-form__input label:not(.form__label) {
          min-width: 44px;
          padding-inline: 12px;
        }
      }
    `;
    document.head.appendChild(style);
  }

  function resolveOverlayPlacement(overlay, targetKey) {
    if (targetKey === "name") {
      return {
        scale: Number(overlay.nameScale || 100),
        offsetX: Number(overlay.nameOffsetX || 0),
        offsetY: Number(overlay.nameOffsetY || -8),
      };
    }

    if (targetKey === "number") {
      return {
        scale: Number(overlay.numberScale || 100),
        offsetX: Number(overlay.numberOffsetX || 0),
        offsetY: Number(overlay.numberOffsetY || 10),
      };
    }

    return {
      scale: Number(overlay.scale || 100),
      offsetX: Number(overlay.offsetX || 0),
      offsetY: Number(overlay.offsetY || 0),
    };
  }

  function resolveOverlayBackgroundColor(overlay) {
    if (!(overlay && overlay.boxEnabled === true && Number(overlay.boxFillOpacity || 0) > 0)) {
      return "transparent";
    }

    const hex = String(overlay.boxFillColor || "").trim().replace(/^#/, "");
    if (!/^[0-9a-f]{6}$/i.test(hex)) {
      return overlay.boxFillColor || "transparent";
    }

    const red = Number.parseInt(hex.slice(0, 2), 16);
    const green = Number.parseInt(hex.slice(2, 4), 16);
    const blue = Number.parseInt(hex.slice(4, 6), 16);
    const alpha = Math.max(0, Math.min(1, Number(overlay.boxFillOpacity || 0) / 100));
    return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
  }

  function resolveTextAlignment(value) {
    const normalized = String(value || "").trim().toLowerCase();
    if (normalized === "left") {
      return "flex-start";
    }

    if (normalized === "right") {
      return "flex-end";
    }

    return "center";
  }

  function collectActiveTrykkOverlays(item, useSecondary) {
    if (!Array.isArray(item.trykkOptions)) {
      return [];
    }

    return item.trykkOptions
      .flatMap((option) => {
        const box = findTrykkBox(option.code);
        const overlay = resolveOverlayForPresentation(option, useSecondary);
        if (!overlay || overlay.enabled !== true) {
          return [];
        }

        if (!(box instanceof HTMLElement)) {
          if (
            option.defaultSelected === true ||
            option.required === true ||
            option.locked === true
          ) {
            return buildOverlayEntries(option, overlay, {
              single: overlay.text || option.title || "",
              name: overlay.nameText || "NAVN",
              number: overlay.numberText || "00",
            });
          }

          return [];
        }

        const enabled = box.querySelector(".js-trykk-enabled");
        if (!(enabled instanceof HTMLInputElement) || enabled.checked !== true) {
          return [];
        }

        return buildOverlayEntries(option, overlay, resolveOverlayTextValues(box, option, overlay));
      })
      .filter(Boolean);
  }

  function resolveOverlayForPresentation(option, useSecondary) {
    if (!useSecondary) {
      return option && option.overlay ? option.overlay : null;
    }

    if (option && option.usePrimaryOverlayOnSecondary !== false) {
      return option.overlay || null;
    }

    return option.secondaryOverlay || option.overlay || null;
  }

  function resolveOverlayTextValues(box, option, overlay) {
    const emptyState = {
      single: "",
      name: overlay && overlay.nameText ? overlay.nameText : "",
      number: overlay && overlay.numberText ? overlay.numberText : "",
    };

    if (!overlay || overlay.kind !== "text") {
      return emptyState;
    }

    const inputType = normalizeToken(option && option.inputType ? option.inputType : "none");
    if (!inputType || inputType === "none") {
      return {
        single: overlay.text || option.title || "",
        name: "",
        number: "",
      };
    }

    if (inputType === "text" || inputType === "number_range") {
      const value = Array.from(box.querySelectorAll(".js-trykk-unit"))
        .map((input) => String(input.value || "").trim())
        .find(Boolean);
      return {
        single: value || "",
        name: "",
        number: "",
      };
    }

    if (inputType === "name_number") {
      const nameValue = Array.from(box.querySelectorAll(".js-trykk-name"))
        .map((input) => String(input.value || "").trim())
        .find(Boolean);
      const numberValue = Array.from(box.querySelectorAll(".js-trykk-number"))
        .map((input) => String(input.value || "").trim())
        .find(Boolean);
      return {
        single: "",
        name: nameValue || "",
        number: numberValue || "",
      };
    }

    return {
      single: overlay.text || option.title || "",
      name: "",
      number: "",
    };
  }

  function buildOverlayEntries(option, overlay, textValues) {
    if (!overlay || overlay.enabled !== true) {
      return [];
    }

    if (overlay.kind === "image") {
      return [
        {
          option,
          overlay,
          targetKey: "single",
          contentText: option.title || "Trykk",
        },
      ];
    }

    const inputType = normalizeToken(option && option.inputType ? option.inputType : "none");
    if (inputType === "name_number") {
      const entries = [];
      if (textValues && textValues.name) {
        entries.push({
          option,
          overlay,
          targetKey: "name",
          contentText: textValues.name,
        });
      }
      if (textValues && textValues.number) {
        entries.push({
          option,
          overlay,
          targetKey: "number",
          contentText: textValues.number,
        });
      }
      return entries;
    }

    if (textValues && textValues.single) {
      return [
        {
          option,
          overlay,
          targetKey: "single",
          contentText: textValues.single,
        },
      ];
    }

    if (inputType === "none") {
      return [
        {
          option,
          overlay,
          targetKey: "single",
          contentText: overlay.text || option.title || "TRYKK",
        },
      ];
    }

    return [];
  }

  function findTrykkBox(code) {
    const normalizedCode = normalizeToken(code);
    if (!normalizedCode) {
      return null;
    }

    return Array.from(document.querySelectorAll("[data-trykk-box]")).find((box) => {
      if (!(box instanceof HTMLElement)) {
        return false;
      }

      if (box.hidden || box.style.display === "none") {
        return false;
      }

      return normalizeToken(box.dataset.trykkCode || "") === normalizedCode;
    });
  }

  function findTrykkBoxes(code) {
    const normalizedCode = normalizeToken(code);
    if (!normalizedCode) {
      return [];
    }

    return Array.from(document.querySelectorAll("[data-trykk-box]")).filter((box) => {
      if (!(box instanceof HTMLElement)) {
        return false;
      }

      return normalizeToken(box.dataset.trykkCode || "") === normalizedCode;
    });
  }

  function readTrykkRootConfig(root) {
    const configNode = root.querySelector("script[data-trykk-config]");
    if (!(configNode instanceof HTMLScriptElement)) {
      return null;
    }

    try {
      return JSON.parse(configNode.textContent || "{}");
    } catch (_error) {
      return null;
    }
  }

  function findSourceTrykkOption(option, sourceConfig) {
    if (!Array.isArray(sourceConfig && sourceConfig.options)) {
      return null;
    }

    const optionCode = normalizeToken(option && option.code ? option.code : "");
    const optionTitle = normalizeToken(option && option.title ? option.title : "");

    return (
      sourceConfig.options.find((entry) => normalizeToken(entry && entry.kode ? entry.kode : "") === optionCode) ||
      sourceConfig.options.find((entry) =>
        normalizeToken(entry && entry.tittel ? entry.tittel : "") === optionTitle,
      ) ||
      null
    );
  }

  function resolveTrykkPackTitle(option, sourceConfig) {
    const sourceOption = findSourceTrykkOption(option, sourceConfig);
    const packTitle =
      (option && option.packTitle) ||
      (sourceOption &&
        sourceOption.trykk_pack &&
        typeof sourceOption.trykk_pack.tittel === "string" &&
        sourceOption.trykk_pack.tittel) ||
      "Trykk";

    return String(packTitle || "Trykk").trim() || "Trykk";
  }

  function resolveFallbackTrykkDetails(option, sourceConfig) {
    const sourceOption = findSourceTrykkOption(option, sourceConfig);
    const trykkProduct = sourceOption && sourceOption.trykkprodukt ? sourceOption.trykkprodukt : {};

    return {
      code: String(option && option.code ? option.code : sourceOption && sourceOption.kode ? sourceOption.kode : "")
        .trim(),
      title: String(
        (option && option.title) || (sourceOption && sourceOption.tittel) || "Trykk",
      ).trim(),
      inputType: String(
        (option && option.inputType) || (sourceOption && sourceOption.inputtype) || "none",
      )
        .trim()
        .toLowerCase(),
      required: option && option.required === true,
      defaultSelected: option && option.defaultSelected === true,
      locked: option && option.locked === true,
      maxLength:
        option && typeof option.maxLength === "number"
          ? Number(option.maxLength)
          : toOptionalNumber(sourceOption && sourceOption.makslengdetekst),
      rangeMin:
        option && typeof option.rangeMin === "number"
          ? Number(option.rangeMin)
          : toOptionalNumber(sourceOption && sourceOption.minverdi),
      rangeMax:
        option && typeof option.rangeMax === "number"
          ? Number(option.rangeMax)
          : toOptionalNumber(sourceOption && sourceOption.maxverdi),
      defaultNumberValue:
        option && typeof option.defaultNumberValue === "number"
          ? Number(option.defaultNumberValue)
          : toOptionalNumber(sourceOption && sourceOption.defaultnumbervalue),
      numberUiMode: String(
        (option && option.numberUiMode) || (sourceOption && sourceOption.numberuimode) || "input",
      )
        .trim()
        .toLowerCase(),
      variantId: String(
        trykkProduct.variant_id ||
          trykkProduct.variantId ||
          trykkProduct.id ||
          "",
      ).trim(),
      variantSku: String(trykkProduct.variant_sku || trykkProduct.sku || "").trim(),
      priceText: String(trykkProduct.price_text || "").trim(),
    };
  }

  function createFallbackTrykkBox(option, sourceConfig) {
    const details = resolveFallbackTrykkDetails(option, sourceConfig);
    if (!details.code) {
      return null;
    }

    const box = document.createElement("div");
    box.className = "trykk-box";
    box.dataset.trykkBox = "1";
    box.dataset.trykkCode = details.code;
    box.dataset.inputType = details.inputType;
    box.dataset.required = String(details.required);
    box.dataset.defaultOn = String(details.defaultSelected || details.required || details.locked);
    box.dataset.trykkShopifyVariantId = details.variantId;
    box.dataset.trykkVariantSku = details.variantSku;
    box.dataset.clubPageFallback = "true";

    if (typeof details.maxLength === "number") {
      box.dataset.maxLen = String(details.maxLength);
    }
    if (typeof details.rangeMin === "number") {
      box.dataset.min = String(details.rangeMin);
    }
    if (typeof details.rangeMax === "number") {
      box.dataset.max = String(details.rangeMax);
    }
    if (typeof details.defaultNumberValue === "number") {
      box.dataset.defaultNumberValue = String(details.defaultNumberValue);
    }
    if (details.numberUiMode) {
      box.dataset.numberUiMode = details.numberUiMode;
    }

    const head = document.createElement("div");
    head.className = "trykk-head";

    const left = document.createElement("div");
    left.className = "trykk-left";

    const title = document.createElement("div");
    title.className = "trykk-title";
    title.textContent = details.title;
    left.appendChild(title);

    if (details.priceText) {
      const price = document.createElement("div");
      price.className = "trykk-price";
      price.textContent = `+ ${details.priceText}`;
      left.appendChild(price);
    }

    const toggle = document.createElement("label");
    toggle.className = "trykk-toggle";

    const checkbox = document.createElement("input");
    checkbox.type = "checkbox";
    checkbox.className = "js-trykk-enabled";

    const toggleText = document.createElement("span");
    toggleText.textContent = "Legg til";

    const hint = document.createElement("span");
    hint.className = "trykk-hint js-trykk-hint";

    toggle.appendChild(checkbox);
    toggle.appendChild(toggleText);
    toggle.appendChild(hint);

    head.appendChild(left);
    head.appendChild(toggle);

    const meta = document.createElement("div");
    meta.className = "trykk-meta";

    const metaLabel = document.createElement("div");
    metaLabel.className = "trykk-meta-label";
    metaLabel.textContent = "Antall trykk:";

    const metaValue = document.createElement("input");
    metaValue.className = "trykk-meta-input js-trykk-qty";
    metaValue.type = "text";
    metaValue.value = "0";
    metaValue.readOnly = true;

    meta.appendChild(metaLabel);
    meta.appendChild(metaValue);

    const extra = document.createElement("div");
    extra.className = "js-trykk-extra";
    extra.style.display = "none";

    const inputs = document.createElement("div");
    inputs.className = "js-trykk-inputs";
    extra.appendChild(inputs);

    const help = document.createElement("div");
    help.className = "trykk-help";
    help.textContent = buildFallbackHelpText(details.inputType);
    extra.appendChild(help);

    box.appendChild(head);
    box.appendChild(meta);
    box.appendChild(extra);

    return box;
  }

  function bindFallbackTrykkBox(box, option, sourceConfig) {
    if (!(box instanceof HTMLElement) || box.dataset.clubPageFallbackBound === "true") {
      return;
    }

    box.dataset.clubPageFallbackBound = "true";
    const enabled = box.querySelector(".js-trykk-enabled");
    if (!(enabled instanceof HTMLInputElement)) {
      return;
    }

    const sync = () => syncFallbackTrykkBox(box, option, sourceConfig);

    enabled.addEventListener("change", sync);
    document.addEventListener("input", (event) => {
      const target = event.target;
      if (target instanceof HTMLElement && target.matches('input[name="quantity"], .quantity__input')) {
        sync();
      }
    });
    document.addEventListener("change", (event) => {
      const target = event.target;
      if (target instanceof HTMLElement && target.matches('input[name="quantity"], .quantity__input')) {
        sync();
      }
    });
    sync();
  }

  function updateFallbackTrykkBox(box, option, sourceConfig) {
    if (!(box instanceof HTMLElement)) {
      return;
    }

    const details = resolveFallbackTrykkDetails(option, sourceConfig);
    box.dataset.inputType = details.inputType;
    box.dataset.required = String(details.required);
    box.dataset.defaultOn = String(details.defaultSelected || details.required || details.locked);
    box.dataset.trykkShopifyVariantId = details.variantId;
    box.dataset.trykkVariantSku = details.variantSku;
    box.dataset.numberUiMode = details.numberUiMode;

    if (typeof details.maxLength === "number") {
      box.dataset.maxLen = String(details.maxLength);
    } else {
      delete box.dataset.maxLen;
    }
    if (typeof details.rangeMin === "number") {
      box.dataset.min = String(details.rangeMin);
    } else {
      delete box.dataset.min;
    }
    if (typeof details.rangeMax === "number") {
      box.dataset.max = String(details.rangeMax);
    } else {
      delete box.dataset.max;
    }
    if (typeof details.defaultNumberValue === "number") {
      box.dataset.defaultNumberValue = String(details.defaultNumberValue);
    } else {
      delete box.dataset.defaultNumberValue;
    }

    const title = box.querySelector(".trykk-title");
    if (title instanceof HTMLElement) {
      title.textContent = details.title;
    }

    const price = box.querySelector(".trykk-price");
    if (details.priceText) {
      if (price instanceof HTMLElement) {
        price.textContent = `+ ${details.priceText}`;
      } else {
        const left = box.querySelector(".trykk-left");
        if (left instanceof HTMLElement) {
          const priceNode = document.createElement("div");
          priceNode.className = "trykk-price";
          priceNode.textContent = `+ ${details.priceText}`;
          left.appendChild(priceNode);
        }
      }
    } else if (price instanceof HTMLElement) {
      price.remove();
    }

    const help = box.querySelector(".trykk-help");
    if (help instanceof HTMLElement) {
      help.textContent = buildFallbackHelpText(details.inputType);
    }

    bindFallbackTrykkBox(box, option, sourceConfig);
    syncFallbackTrykkBox(box, option, sourceConfig);
  }

  function syncFallbackTrykkBox(box, option, sourceConfig) {
    if (!(box instanceof HTMLElement) || box.dataset.clubPageFallback !== "true") {
      return;
    }

    const enabled = box.querySelector(".js-trykk-enabled");
    const extra = box.querySelector(".js-trykk-extra");
    const hint = box.querySelector(".js-trykk-hint");
    const quantityView = box.querySelector(".js-trykk-qty");
    const quantityInput = getQuantityInput();

    if (
      !(enabled instanceof HTMLInputElement) ||
      !(extra instanceof HTMLElement) ||
      !(quantityView instanceof HTMLInputElement)
    ) {
      return;
    }

    const quantity = getQuantityValue(quantityInput);
    quantityView.value = String(quantity);

    if (!enabled.checked) {
      extra.style.display = "none";
      if (hint instanceof HTMLElement) {
        hint.textContent = "";
      }
      clearFallbackTrykkInputs(box);
      return;
    }

    extra.style.display = "";
    if (hint instanceof HTMLElement) {
      hint.textContent = `(følger antall: ${quantity})`;
    }
    renderFallbackTrykkInputs(box, option, sourceConfig, quantity);
  }

  function renderFallbackTrykkInputs(box, option, sourceConfig, quantity) {
    const container = box.querySelector(".js-trykk-inputs");
    if (!(container instanceof HTMLElement)) {
      return;
    }

    const details = resolveFallbackTrykkDetails(option, sourceConfig);
    const renderSignature = JSON.stringify({
      inputType: details.inputType,
      quantity,
      maxLength: details.maxLength ?? "",
      rangeMin: details.rangeMin ?? "",
      rangeMax: details.rangeMax ?? "",
      defaultNumberValue: details.defaultNumberValue ?? "",
      numberUiMode: details.numberUiMode ?? "",
    });

    if (details.inputType === "none" || quantity <= 0) {
      container.innerHTML = "";
      container.dataset.renderSignature = renderSignature;
      return;
    }

    if (container.dataset.renderSignature === renderSignature && container.childElementCount > 0) {
      return;
    }

    const preserved = readFallbackTrykkValues(box, details.inputType);
    container.innerHTML = "";

    for (let index = 0; index < quantity; index += 1) {
      const row = document.createElement("div");
      row.className = "trykk-row";

      const counter = document.createElement("div");
      counter.className = "trykk-idx";
      counter.textContent = `${index + 1}.`;
      row.appendChild(counter);

      if (details.inputType === "text") {
        const input = document.createElement("input");
        input.type = "text";
        input.className = "js-trykk-unit";
        input.placeholder = "Navn";
        input.style.flex = "1";
        if (typeof details.maxLength === "number") {
          input.maxLength = details.maxLength;
        }
        input.value = String(preserved[index] || "");
        row.appendChild(input);
        container.appendChild(row);
        continue;
      }

      if (details.inputType === "number_range") {
        row.appendChild(buildFallbackNumberInput(details, preserved[index]));
        container.appendChild(row);
        continue;
      }

      if (details.inputType === "name_number") {
        const currentValue = preserved[index] && typeof preserved[index] === "object"
          ? preserved[index]
          : {};
        const nameInput = document.createElement("input");
        nameInput.type = "text";
        nameInput.className = "js-trykk-unit js-trykk-name";
        nameInput.placeholder = "Navn";
        nameInput.style.flex = "1";
        if (typeof details.maxLength === "number") {
          nameInput.maxLength = details.maxLength;
        }
        nameInput.value = String(currentValue.name || "");
        row.appendChild(nameInput);

        const numberInput = buildFallbackNumberInput(details, currentValue.number || "");
        numberInput.classList.add("js-trykk-number");
        if (numberInput instanceof HTMLInputElement) {
          numberInput.classList.add("js-trykk-unit");
        } else {
          numberInput.classList.add("js-trykk-unit");
        }
        row.appendChild(numberInput);
        container.appendChild(row);
      }
    }

    container.dataset.renderSignature = renderSignature;
  }

  function buildFallbackNumberInput(details, currentValue) {
    const min = typeof details.rangeMin === "number" ? Number(details.rangeMin) : 0;
    const max = typeof details.rangeMax === "number" ? Number(details.rangeMax) : 99;
    const safeMin = Math.min(min, max);
    const safeMax = Math.max(min, max);
    const defaultValue =
      currentValue !== undefined && currentValue !== null && String(currentValue).trim() !== ""
        ? String(currentValue)
        : typeof details.defaultNumberValue === "number"
          ? String(details.defaultNumberValue)
          : String(safeMin);

    if (details.numberUiMode === "select") {
      const select = document.createElement("select");
      select.style.width = "120px";
      for (let value = safeMin; value <= safeMax; value += 1) {
        const option = document.createElement("option");
        option.value = String(value);
        option.textContent = String(value);
        if (String(value) === defaultValue) {
          option.selected = true;
        }
        select.appendChild(option);
      }
      return select;
    }

    const input = document.createElement("input");
    input.type = "number";
    input.min = String(safeMin);
    input.max = String(safeMax);
    input.className = "js-trykk-unit";
    input.style.width = "120px";
    input.value = defaultValue;
    return input;
  }

  function readFallbackTrykkValues(box, inputType) {
    if (inputType === "text" || inputType === "number_range") {
      return Array.from(box.querySelectorAll(".js-trykk-unit")).map((input) =>
        String(input.value || ""),
      );
    }

    if (inputType === "name_number") {
      return Array.from(box.querySelectorAll(".trykk-row")).map((row) => {
        const nameInput = row.querySelector(".js-trykk-name");
        const numberInput = row.querySelector(".js-trykk-number");
        return {
          name: String(nameInput && "value" in nameInput ? nameInput.value || "" : ""),
          number: String(numberInput && "value" in numberInput ? numberInput.value || "" : ""),
        };
      });
    }

    return [];
  }

  function clearFallbackTrykkInputs(box) {
    const container = box.querySelector(".js-trykk-inputs");
    if (container instanceof HTMLElement) {
      container.innerHTML = "";
    }
  }

  function getQuantityInput() {
    return (
      document.querySelector('input.quantity__input[name="quantity"]') ||
      document.querySelector('input[name="quantity"]')
    );
  }

  function getQuantityValue(input) {
    const parsed = Number.parseInt(
      String(input && typeof input === "object" && "value" in input ? input.value : "1"),
      10,
    );
    if (!Number.isFinite(parsed) || parsed <= 0) {
      return 1;
    }

    return parsed;
  }

  function buildFallbackHelpText(inputType) {
    if (inputType === "text") {
      return "Skriv inn navn (en linje per trykk).";
    }
    if (inputType === "number_range") {
      return "Velg nummer (en linje per trykk).";
    }
    if (inputType === "name_number") {
      return "Skriv inn navn og velg nummer (en linje per trykk).";
    }
    return "Ingen input – valget legges til i handlekurven.";
  }

  function findOrCreateFallbackPack(mount, packTitle, code) {
    const packKey = normalizeToken(packTitle || code || "trykk");
    const existing = Array.from(mount.querySelectorAll("[data-trykk-pack]")).find((pack) => {
      if (!(pack instanceof HTMLElement)) {
        return false;
      }

      return pack.dataset.clubPageFallbackPack === packKey;
    });

    if (existing instanceof HTMLElement) {
      return existing;
    }

    const pack = document.createElement("div");
    pack.className = "trykk-pack";
    pack.dataset.trykkPack = `club-page-${packKey}`;
    pack.dataset.clubPageFallbackPack = packKey;

    if (packTitle) {
      const title = document.createElement("div");
      title.className = "trykk-pack-title";
      title.textContent = packTitle;
      pack.appendChild(title);
    }

    mount.appendChild(pack);
    return pack;
  }

  function cleanupFallbackTrykkBoxes(activeCodes) {
    Array.from(document.querySelectorAll('[data-trykk-box][data-club-page-fallback="true"]')).forEach((box) => {
      if (!(box instanceof HTMLElement)) {
        return;
      }

      const code = normalizeToken(box.dataset.trykkCode || "");
      if (!activeCodes.has(code)) {
        removeFallbackTrykkBox(box);
      }
    });
  }

  function cleanupEmptyFallbackPacks() {
    Array.from(document.querySelectorAll("[data-club-page-fallback-pack]")).forEach((pack) => {
      if (!(pack instanceof HTMLElement)) {
        return;
      }

      const boxes = Array.from(pack.querySelectorAll("[data-trykk-box]")).filter((box) =>
        box instanceof HTMLElement,
      );
      if (boxes.length === 0) {
        pack.remove();
      }
    });
  }

  function removeFallbackTrykkBox(box) {
    if (!(box instanceof HTMLElement)) {
      return;
    }

    const pack = box.closest("[data-club-page-fallback-pack]");
    box.remove();
    if (pack instanceof HTMLElement) {
      const hasBoxes = pack.querySelector("[data-trykk-box]");
      if (!hasBoxes) {
        pack.remove();
      }
    }
  }

  function toOptionalNumber(value) {
    if (value === undefined || value === null || value === "") {
      return undefined;
    }

    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  }

  function resolvePresentation(item, selectionState) {
    const selection =
      selectionState && typeof selectionState === "object"
        ? selectionState
        : resolveSelectionState(item);
    const selectedValues = selection.selectedColorCandidates;
    const secondaryEntries = item.secondaryColorRawValue
      ? buildAllowedColorEntries({ secondaryColorRawValue: item.secondaryColorRawValue })
      : [];
    let useSecondary =
      Boolean(item.secondaryDisplayImageUrl) && selection.selectedColorKind === "secondary";

    if (
      !useSecondary &&
      Boolean(item.secondaryDisplayImageUrl) &&
      selection.selectedColorKind !== "primary" &&
      secondaryEntries.length > 0
    ) {
      useSecondary = matchesAnyAllowedColor(selectedValues, secondaryEntries);
    }

    return {
      useSecondary,
      imageUrl: useSecondary && item.secondaryDisplayImageUrl
        ? item.secondaryDisplayImageUrl
        : item.displayImageUrl,
    };
  }

  function buildAllowedColorEntries(item) {
    const entries = [];
    const primaryDisplayValue = item && item.secondaryColorRawValue
      ? item.selectedColorRawValue
      : item && item.colorDisplay;
    const primaryEntry = createAllowedColorEntry(item && item.selectedColorRawValue, primaryDisplayValue);
    const secondaryEntry = createAllowedColorEntry(
      item && item.secondaryColorRawValue,
      item && item.secondaryColorRawValue,
    );

    if (primaryEntry) {
      entries.push(primaryEntry);
    }
    if (secondaryEntry) {
      entries.push(secondaryEntry);
    }

    return entries;
  }

  function getSelectedColorCandidates() {
    const root = getMainProductRoot();
    if (!(root instanceof HTMLElement)) {
      return [];
    }

    const values = [];
    const seen = new Set();
    const pushValue = (value) => {
      const nextValue = String(value || "").trim();
      if (!nextValue) {
        return;
      }

      const key = normalizeToken(nextValue);
      if (seen.has(key)) {
        return;
      }

      seen.add(key);
      values.push(nextValue);
    };
    const radioFields = Array.from(root.querySelectorAll("fieldset")).filter((fieldset) =>
      isColorField(fieldset),
    );

    radioFields.forEach((fieldset) => {
      const checked = fieldset.querySelector('input[type="radio"]:checked');
      if (!(checked instanceof HTMLInputElement)) {
        return;
      }

      const label =
        (checked.id && fieldset.querySelector(`label[for="${cssEscape(checked.id)}"]`)) ||
        checked.closest("label");
      pushValue(label?.textContent);
      pushValue(checked.value);
      pushValue(checked.getAttribute("data-option-value"));
      pushValue(checked.dataset.optionValue);
      pushValue(checked.getAttribute("data-value"));
    });

    const selects = Array.from(root.querySelectorAll("select")).filter((select) =>
      isColorSelect(select),
    );

    selects.forEach((select) => {
      const option = select.selectedOptions && select.selectedOptions[0];
      pushValue(option && option.textContent);
      pushValue(option && option.value);
      pushValue(select.value);
    });

    return values;
  }

  function resolveSelectionState(item) {
    const selectedVariantLegacyId = getSelectedVariantLegacyId();
    const selectedColorCandidates = getSelectedColorCandidates();
    const primaryVariantIds = new Set(
      (Array.isArray(item && item.selectedVariantLegacyIds) ? item.selectedVariantLegacyIds : [])
        .map((value) => normalizeIdString(value))
        .filter(Boolean),
    );
    const primaryEntries = buildAllowedColorEntries({
      selectedColorRawValue: item && item.selectedColorRawValue,
      colorDisplay: item && item.secondaryColorRawValue ? item.selectedColorRawValue : item && item.colorDisplay,
    });
    const secondaryEntries = item && item.secondaryColorRawValue
      ? buildAllowedColorEntries({ secondaryColorRawValue: item.secondaryColorRawValue })
      : [];

    let selectedColorKind = "";
    if (selectedVariantLegacyId) {
      if (primaryVariantIds.has(selectedVariantLegacyId)) {
        selectedColorKind = "primary";
      } else if (
        item &&
        item.secondaryDisplayImageUrl &&
        matchesAnyAllowedColor(selectedColorCandidates, secondaryEntries)
      ) {
        selectedColorKind = "secondary";
      } else if (matchesAnyAllowedColor(selectedColorCandidates, primaryEntries)) {
        selectedColorKind = "primary";
      }
    } else if (matchesAnyAllowedColor(selectedColorCandidates, secondaryEntries)) {
      selectedColorKind = "secondary";
    } else if (matchesAnyAllowedColor(selectedColorCandidates, primaryEntries)) {
      selectedColorKind = "primary";
    }

    return {
      selectedVariantLegacyId,
      selectedColorCandidates,
      selectedColorKind,
    };
  }

  function getSelectedVariantLegacyId() {
    const root = getMainProductRoot();
    const searchRoot = root instanceof HTMLElement ? root : document;
    const inputs = Array.from(
      searchRoot.querySelectorAll(
        [
          'product-form input[name="id"]',
          'form[action*="/cart/add"] input[name="id"]',
          'input[name="id"]',
          'select[name="id"]',
        ].join(", "),
      ),
    );

    for (const input of inputs) {
      if (!(input instanceof HTMLInputElement || input instanceof HTMLSelectElement)) {
        continue;
      }

      const value = normalizeIdString(input.value);
      if (value) {
        return value;
      }
    }

    return normalizeIdString(new URLSearchParams(window.location.search).get("variant"));
  }

  function buildColorTokens(rawValue, displayValue) {
    const tokens = new Set();

    for (const value of [rawValue, displayValue]) {
      const normalized = String(value || "").trim();
      if (!normalized) {
        continue;
      }

      tokens.add(normalizeToken(normalized));
      addColorAliases(tokens, normalized);

      const dashMatch = normalized.match(/^(\d{2,6})[-/ ]+(.+)$/);
      if (dashMatch) {
        tokens.add(normalizeToken(dashMatch[1]));
        tokens.add(normalizeToken(dashMatch[2]));
        addColorAliases(tokens, dashMatch[2]);
      }

      const colonMatch = normalized.match(/^([^:]+):\s*(.+)$/);
      if (colonMatch) {
        tokens.add(normalizeToken(colonMatch[1]));
        tokens.add(normalizeToken(colonMatch[2]));
        addColorAliases(tokens, colonMatch[2]);
      }
    }

    return tokens;
  }

  function addColorAliases(tokens, value) {
    const normalized = normalizeToken(value);
    if (!normalized) {
      return;
    }

    const groups = [
      ["white", "hvit", "off white", "egret"],
      ["black", "svart", "sort", "ink", "india ink"],
      ["blue", "bla", "navy", "marine", "true blue"],
      ["green", "gronn", "grape leaf", "evergreen", "jelly bean", "olive", "leaf"],
      ["red", "rod", "true red"],
      ["yellow", "gul", "sports yellow", "blazing yellow"],
      ["pink", "rosa", "cotton candy"],
      ["grey", "gray", "gra", "melange", "grey melange", "egret melange"],
    ];

    groups.forEach((group) => {
      if (!group.some((entry) => containsNormalizedPhrase(normalized, entry))) {
        return;
      }

      group.forEach((entry) => tokens.add(normalizeToken(entry)));
    });
  }

  function createAllowedColorEntry(rawValue, displayValue) {
    const raw = normalizeToken(rawValue);
    const display = normalizeToken(displayValue);
    const name = extractColorName(raw) || extractColorName(display);
    const code = extractColorCode(raw) || extractColorCode(display);
    const aliases = new Set();

    [rawValue, displayValue, name].forEach((value) => {
      const group = findColorAliasGroup(value);
      if (!group) {
        return;
      }
      group.forEach((entry) => aliases.add(normalizeToken(entry)));
    });

    if (!raw && !display && !name && !code && aliases.size === 0) {
      return null;
    }

    return {
      raw,
      display,
      name,
      code,
      aliases,
    };
  }

  function findColorAliasGroup(value) {
    const normalized = normalizeToken(value);
    if (!normalized) {
      return null;
    }

    const groups = [
      ["white", "hvit", "off white", "egret"],
      ["black", "svart", "sort", "ink", "india ink"],
      ["blue", "bla", "navy", "marine", "true blue"],
      ["green", "gronn", "grape leaf", "evergreen", "jelly bean", "olive", "leaf"],
      ["red", "rod", "true red"],
      ["yellow", "gul", "sports yellow", "blazing yellow"],
      ["pink", "rosa", "cotton candy"],
      ["grey", "gray", "gra", "melange", "grey melange", "egret melange"],
    ];

    return (
      groups.find((group) => group.some((entry) => containsNormalizedPhrase(normalized, entry))) || null
    );
  }

  function extractColorCode(value) {
    const normalized = normalizeToken(value);
    if (!normalized) {
      return "";
    }

    const match = normalized.match(/^(\d{2,6})(?:[\s/-]+|$)/);
    return match ? match[1] : "";
  }

  function extractColorName(value) {
    const normalized = normalizeToken(value);
    if (!normalized) {
      return "";
    }

    const dashMatch = normalized.match(/^\d{2,6}[\s/-]+(.+)$/);
    if (dashMatch) {
      return dashMatch[1].trim();
    }

    const colonMatch = normalized.match(/^[^:]+:\s*(.+)$/);
    if (colonMatch) {
      return colonMatch[1].trim();
    }

    return normalized;
  }

  function matchesAnyAllowedColor(values, entries) {
    return values.some((value) => matchesAllowedColorValue(value, entries));
  }

  function matchesAllowedColorValue(value, entries) {
    const normalized = normalizeToken(value);
    if (!normalized || !Array.isArray(entries) || entries.length === 0) {
      return false;
    }

    const candidateName = extractColorName(normalized);
    const candidateCode = extractColorCode(normalized);

    return entries.some((entry) => {
      if (!entry) {
        return false;
      }

      if (
        normalized === entry.raw ||
        normalized === entry.display ||
        normalized === entry.name ||
        candidateName === entry.name
      ) {
        return true;
      }

      if (entry.code && (normalized === entry.code || candidateCode === entry.code)) {
        return true;
      }

      if (entry.aliases instanceof Set) {
        const hasSpecificCode = Boolean(entry.code);
        const hasSpecificCompoundName =
          Boolean(entry.name) && /[\s/-]/.test(String(entry.name || "").trim());
        const candidateIsSpecific =
          Boolean(candidateCode) || /[\s/-]/.test(String(candidateName || normalized).trim());

        if ((hasSpecificCode || hasSpecificCompoundName) && candidateIsSpecific) {
          return false;
        }

        if (entry.aliases.has(normalized) || entry.aliases.has(candidateName)) {
          return true;
        }

        if (
          Array.from(entry.aliases).some((alias) =>
            containsNormalizedPhrase(normalized, alias) || containsNormalizedPhrase(candidateName, alias),
          )
        ) {
          return true;
        }
      }

      return false;
    });
  }

  function matchesAnyColorCandidate(values, tokens) {
    return values.some((value) => matchesColorValue(value, tokens));
  }

  function matchesColorValue(value, tokens) {
    const normalized = normalizeToken(value);
    if (!normalized) {
      return false;
    }

    if (tokens.has(normalized)) {
      return true;
    }

    const candidateName = extractColorName(normalized);
    const candidateCode = extractColorCode(normalized);

    return Array.from(tokens).some((token) => {
      if (token === normalized || token === candidateName || token === candidateCode) {
        return true;
      }

      return containsNormalizedPhrase(normalized, token) || containsNormalizedPhrase(token, normalized);
    });
  }

  function containsNormalizedPhrase(haystack, needle) {
    const normalizedHaystack = normalizeToken(haystack);
    const normalizedNeedle = normalizeToken(needle);
    if (!normalizedHaystack || !normalizedNeedle) {
      return false;
    }

    const escapedNeedle = normalizedNeedle.replace(/[.*+?^${}()|[\]\\]/g, "\\$&").replace(/\s+/g, "[\\s/-]+");
    const pattern = new RegExp(`(^|[\\s/-])${escapedNeedle}($|[\\s/-])`);
    return pattern.test(normalizedHaystack);
  }

  function isColorField(fieldset) {
    const label = [
      fieldset.getAttribute("aria-label"),
      fieldset.querySelector("legend")?.textContent,
      fieldset.dataset.optionName,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();

    return label.includes("farge") || label.includes("color") || label.includes("colour");
  }

  function isColorSelect(select) {
    const id = select.getAttribute("id") || "";
    const name = select.getAttribute("name") || "";
    const label =
      (id ? document.querySelector(`label[for="${cssEscape(id)}"]`)?.textContent : "") || "";
    const combined = `${id} ${name} ${label}`.toLowerCase();
    return combined.includes("farge") || combined.includes("color") || combined.includes("colour");
  }

  function normalizeToken(value) {
    return String(value || "")
      .normalize("NFKD")
      .replace(/[^\w\s/-]+/g, "")
      .trim()
      .toLowerCase();
  }

  function normalizeIdString(value) {
    const normalized = String(value || "").trim();
    if (!normalized) {
      return "";
    }

    const match = normalized.match(/(\d{6,})$/);
    return match ? match[1] : normalized;
  }

  function cssEscape(value) {
    if (window.CSS && typeof window.CSS.escape === "function") {
      return window.CSS.escape(value);
    }

    return String(value).replace(/[^a-zA-Z0-9_-]/g, "\\$&");
  }
})();
